import { useMemo } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import clsx from 'clsx';
import { BYTES } from 'constants/files';

import { IImageUploadField } from 'interfaces/files';

import { ActiveDropzone } from './ActiveDropzone';
import { StatusDropzone } from './StatusDropzone';
import { DROPZONE, DROPZONE_STATUS, IFileDropzoneProps } from './types';
import { generateErrorsMessage } from './utils';

import styles from './FileDropzone.module.scss';

const FileDropzone = <T extends IImageUploadField>({
  name,
  maxSize,
  multiple = false,
  disabled = false,
  isLoading = false,
  placeholder,
  acceptedFormats,
  className,
  wrapperClassName,
  errorText,
  files = [],
  width,
  height,
  onFileUpload,
  onDelete,
}: IFileDropzoneProps<T>) => {
  const { t } = useTranslation();
  const placeholderText = placeholder ? placeholder : t('uploadImage')
  const isSmallDropzone = (height ?? 0) < 80;

  const maxFileSize = useMemo(() => (maxSize ? maxSize * BYTES * BYTES : undefined), [maxSize]);

  const { getRootProps, getInputProps, open, fileRejections, isDragActive } = useDropzone({
    accept: acceptedFormats,
    maxSize: maxFileSize,
    multiple: multiple,
    onDrop: onFileUpload,
    disabled: disabled,
  });

  const getMessage = (message: string) => (isSmallDropzone ? placeholderText : message);

  const errors = useMemo(() => generateErrorsMessage(fileRejections as FileRejection[]), [fileRejections]);

  const isError = errorText ?? errors.length > 0;

  const dropzoneClasses = clsx(styles.dropzone, {
    [styles.active]: isDragActive && !isLoading && !isError,
    [styles.withFiles]: files.length > 0,
    [styles.error]: isError,
    [styles.loading]: isLoading,
    [styles.multiple]: multiple,
    [styles.disabled]: disabled,
    className,
  });

  const renderStatusOrDropzoneContent = () => {
    if (isLoading)
      return (
        <StatusDropzone
          status={DROPZONE_STATUS.loading}
          message={getMessage('Uploading...')}
          isSmallDropzone={isSmallDropzone}
        />
      );
    if (isError)
      return (
        <StatusDropzone
          status={DROPZONE_STATUS.error}
          message={errorText || errors[0].errors[0]}
          isSmallDropzone={isSmallDropzone}
        />
      );
    if (isDragActive)
      return (
        <StatusDropzone
          status={DROPZONE_STATUS.dragActive}
          message={getMessage('Drag&Drop image')}
          isSmallDropzone={isSmallDropzone}
        />
      );
    if (disabled)
      return (
        <StatusDropzone status={DROPZONE_STATUS.disabled} message={placeholderText} isSmallDropzone={isSmallDropzone} />
      );
    return files.length ? (
      <ActiveDropzone files={files} onDelete={onDelete} isMultiple={multiple} />
    ) : (
      <StatusDropzone status={DROPZONE_STATUS.default} message={placeholderText} isSmallDropzone={isSmallDropzone} />
    );
  };

  const cssVariables = {
    '--dropzone-width': width ? `${width}px` : '100%',
    '--dropzone-height': height ? `${height}px` : '300px',
  } as React.CSSProperties;

  return (
    <section className={clsx(styles.container, wrapperClassName)}>
      <Box
        {...getRootProps({ className: dropzoneClasses })}
        data-id={DROPZONE}
        data-name={name}
        style={cssVariables}
        onClick={open}
      >
        <input {...getInputProps({ name })} disabled={disabled} />
        {renderStatusOrDropzoneContent()}
      </Box>
    </section>
  );
};

export default FileDropzone;
